import { EnableDispenseFlow } from 'common/utils';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compact, map } from 'lodash';
import {
  applyPhysicalCountLockChange,
  fetchLockedInventoryItems,
} from 'store/thunks/workQueueThunks';

import useWebSocket from './useWebSocket';

/**
 * Keeps the physical-count locked-item list live while the caller is mounted (POM-6033).
 *
 * Deltas carry the change; a full re-read on every (re)connect repairs whatever a downed socket
 * missed. Owns the read outright, including the socket-never-connects fallback, so a page open
 * fetches once instead of racing a second copy of itself.
 */
const usePhysicalCountLockSocket = () => {
  const dispatch = useDispatch();
  const { socket } = useWebSocket();
  const fulfillingPharmacies = useSelector(({ auth }) => auth?.fulfillingPharmacies);

  const fulfillingPharmacyIds = useMemo(
    () => compact(map(fulfillingPharmacies, 'fulfillingPharmacyId')),
    [fulfillingPharmacies]
  );

  // The array identity changes on every auth read; the joined ids are what actually decide
  // whether we need to re-subscribe. Ids are compacted above, so an empty list and a list of one
  // blank id cannot collapse onto the same key.
  const facilityKey = fulfillingPharmacyIds.join(',');

  useEffect(() => {
    if (!EnableDispenseFlow || !fulfillingPharmacyIds.length) return undefined;

    // No socket at all — still populate the banner, or the feature would leave it emptier than
    // it was before this change.
    if (!socket) {
      dispatch(fetchLockedInventoryItems(fulfillingPharmacyIds));
      return undefined;
    }

    // A reconnect gives us a new server-side socket, so room membership does not survive it —
    // this has to run on every connect, not once on mount.
    const subscribe = () => {
      socket.emit('join_physical_count_rooms', { fulfillingPharmacyIds });
      dispatch(fetchLockedInventoryItems(fulfillingPharmacyIds));
    };

    const handleLockChanged = ({ action, items }) => {
      dispatch(applyPhysicalCountLockChange({ changeType: action, items }));
    };

    if (socket.connected) {
      subscribe();
    } else {
      // socket.io fires `connect` only once the transport is up. Read now anyway: if it never
      // comes up, this is the only read there will be.
      dispatch(fetchLockedInventoryItems(fulfillingPharmacyIds));
    }

    socket.on('connect', subscribe);
    socket.on('physical_count_lock_changed', handleLockChanged);

    return () => {
      socket.off('connect', subscribe);
      socket.off('physical_count_lock_changed', handleLockChanged);

      if (socket.connected) socket.emit('leave_physical_count_rooms');
    };
    // fulfillingPharmacyIds is covered by facilityKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, dispatch, facilityKey]);
};

export default usePhysicalCountLockSocket;
